import { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, LinearProgress, Alert, Chip, useMediaQuery, useTheme } from '@mui/material';
import { CloudUpload, Error as ErrorIcon } from '@mui/icons-material';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MENU_IDS, PLAYBOOK_URL } from '../constants/constants';
import AppSnackbar from './AppSnackbar';
import { API_BASE_URL, PIPELINE_ID } from '../constants/constants';
import { getAuthToken, getLoggedInUser } from '../utils/auth';
import { COLORS, FONT_HEADING, BUTTON_HOVER_SX } from '../talkTreeShared/Tree/constants/theme';
import UploadHelpDialog from './popups/Uploadhelpdialog';

const ACCEPTED_AUDIO_AND_TRANSCRIPT_TYPES = 'audio/*,audio/mpeg,audio/mp4,audio/x-m4a,audio/m4a,audio/aac,audio/wav,audio/ogg,audio/flac,audio/webm,.mp3,.m4a,.wav,.aac,.ogg,.flac,.opus,.wma,.aiff,.aif,.webm,.vtt,.webvtt,.srt';


const SessionUpload = ({ isTokenExpired, onResourcesClick, setActiveMenu, uploadFormState, setUploadFormState, showSuccessToast }) => {
  const userId = getLoggedInUser() || '';
  const [tutorId, setTutorId] = useState('');
  const [clientId] = useState('levi-hat-teachers');
  const [uploadSource] = useState('talk-trees-dashboard');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', title, message }

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { title, description, file, duration } = uploadFormState;

  const setTitle = (v) => setUploadFormState(prev => ({ ...prev, title: typeof v === 'function' ? v(prev.title) : v }));
  const setDescription = (v) => setUploadFormState(prev => ({ ...prev, description: typeof v === 'function' ? v(prev.description) : v }));
  const setFile = (v) => setUploadFormState(prev => ({ ...prev, file: typeof v === 'function' ? v(prev.file) : v }));
  const setDuration = (v) => setUploadFormState(prev => ({ ...prev, duration: typeof v === 'function' ? v(prev.duration) : v }));

  const resetForm = () => {
    setUploadFormState({ title: '', description: '', file: null, duration: null, sessionMetadata: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const showErrorToast = (message) => {
    setToast({
      type: 'error',
      title: 'There has been a problem processing your file',
      message: message || 'Please try uploading your file again or another file.',
    });
  };

  const getEmailFromToken = () => {
    try {
      const token = getAuthToken();
      if (!token) return '';
      const parts = token.split('.');
      if (parts.length !== 3) return '';
      const payload = JSON.parse(atob(parts[1]));
      return payload.email || '';
    } catch { return ''; }
  };

  useEffect(() => {
    const email = getEmailFromToken();
    if (email) setTutorId(email);
  }, []);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (ms) => {
    return new Date(ms).toLocaleString(undefined, {
      month: 'numeric', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const isTranscriptFile = (f) => ['vtt', 'webvtt', 'srt'].includes(f.name.split('.').pop().toLowerCase());

  const timestampToSeconds = (ts) => {
    const parts = ts.split(':');
    return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseFloat(parts[2]);
  };

  const parseTimestamps = (content, regex) => {
    const lines = content.split('\n');
    let first = null, last = null;
    for (const line of lines) {
      const match = line.match(regex);
      if (match) {
        const start = timestampToSeconds(match[1].replace(',', '.'));
        const end = timestampToSeconds(match[2].replace(',', '.'));
        if (first === null) first = start;
        last = end;
      }
    }
    if (first !== null && last !== null && last - first > 0) return { duration: last - first };
    throw new Error('No valid timestamps found');
  };

  const validateFile = async (f) => {
    if (f.size > 100 * 1024 * 1024) {
      mixpanel.track("uploadRejectedFileTooLarge", { fileName: f.name, fileSize: f.size });
      throw new Error('File too large. Max 100MB.');
    }
    if (f.size === 0) {
      mixpanel.track("uploadRejectedFileIsEmpty", { fileName: f.name });
      throw new Error('File is empty');
    }
    const isVideoFile = f.type.startsWith('video/') ||
      f.name.match(/\.(mp4|mkv|avi|mov|wmv|flv|m4v|mpeg|mpg)$/i);
    if (isVideoFile) {
      mixpanel.track("uploadRejectedVideoFileNotSupported", { fileName: f.name, fileType: f.type });
      throw new Error(
        'Video files are not supported. Please upload an audio file (.mp3, .wav, .m4a, etc.) or a transcript (.vtt, .srt).'
      );
    }
    if (isTranscriptFile(f)) {
      const ext = f.name.split('.').pop().toLowerCase();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const regex = ext === 'srt'
              ? /(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/
              : /(\d{2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}\.\d{3})/;
            const result = parseTimestamps(e.target.result, regex);
            resolve({ isValid: true, duration: result.duration, type: 'transcript' });
          } catch (err) {
            mixpanel.track("uploadRejectedValidationFailed", { fileName: f.name, reason: err.message || 'No valid timestamps found' });
            reject(err);
          }
        };
        reader.onerror = () => {
          mixpanel.track("uploadRejectedValidationFailed", { fileName: f.name, reason: 'Failed to read file' });
          reject(new Error('Failed to read file'));
        };
        reader.readAsText(f);
      });
    }
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(f);
      const cleanup = () => URL.revokeObjectURL(url);
      const timeout = setTimeout(() => {
        cleanup();
        // ---- MIXPANEL CHANGE: untracked validation-timeout path. --------------
        mixpanel.track("uploadRejectedValidationFailed", { fileName: f.name, reason: 'Validation timeout' });
        reject(new Error('Validation timeout'));
      }, 15000);
      const tryAudio = () => {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.addEventListener('loadedmetadata', () => {
          clearTimeout(timeout); cleanup();
          if (audio.duration > 0 && isFinite(audio.duration)) resolve({ isValid: true, duration: audio.duration, type: 'audio' });
          else {
            mixpanel.track("uploadRejectedValidationFailed", { fileName: f.name, reason: 'File appears corrupted' });
            reject(new Error('File appears corrupted'));
          }
        }, { once: true });
        audio.addEventListener('error', () => {
          clearTimeout(timeout); cleanup();
          mixpanel.track("uploadRejectedValidationFailed", { fileName: f.name, reason: 'Not a valid audio format' });
          reject(new Error('Not a valid audio format'));
        }, { once: true });
        audio.addEventListener('error', () => { clearTimeout(timeout); cleanup(); reject(new Error('Not a valid audio format')); }, { once: true });
        audio.src = url;
      };
      tryAudio();
    });
  };

  const generateSessionMetadata = (f, desc, tId, cId, dur, sTitle) => {
    const startTime = new Date(f.lastModified);
    const endTime = new Date(startTime.getTime() + dur * 1000);
    const now = new Date();
    const uploadTimestamp = now.getTime();
    const uploadTime = new Date(uploadTimestamp - now.getTimezoneOffset() * 60000).toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const nameNoExt = f.name.substring(0, f.name.lastIndexOf('.'));
    const ext = f.name.substring(f.name.lastIndexOf('.'));
    // Title is trimmed here so leading/trailing spaces never reach the presign
    // request, the S3 metadata header, or the analysis status record.
    return { title: sTitle.trim(), description: desc.trim(), tutorId: tId.trim(), clientId: cId, startTime: startTime.getTime(), endTime: endTime.getTime(), duration: dur, fileName: `${nameNoExt}_${uploadTime}${ext}`, fileSize: f.size, fileType: f.type, uploadTimestamp };
  };

  // Only validate the file and store file + duration. Metadata is generated at upload time
  // so that title/description entered after file selection are always captured correctly.
  const handleFile = async (selectedFile) => {
    if (!tutorId.trim()) { setWarning('Unable to retrieve email. Please log in again.'); return; }
    setError(''); setWarning(''); setFile(null); setDuration(null);
    try {
      const result = await validateFile(selectedFile);
      setFile(selectedFile);
      setDuration(result.duration);
    } catch (err) {
      setError(err.message);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) handleFile(dropped[0]);
  }, [tutorId]);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length > 0) handleFile(selected[0]);
  };

  const removeFile = () => {
    setFile(null); setDuration(null); setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadToS3 = async (f, sessionId, metadata) => {
    if (isTokenExpired) await isTokenExpired();
    const authToken = getAuthToken();
    const { clientId, startTime, endTime, tutorId, title } = metadata;
    const qs = new URLSearchParams({ clientId, sessionId, startTime, endTime, tutorId, title });
    const presignRes = await fetch(`${API_BASE_URL}v1/mediaLocation?${qs}`, { headers: { Authorization: authToken } });
    if (!presignRes.ok) throw new Error(await presignRes.text());
    const { url } = await presignRes.json();
    const putRes = await fetch(url, { method: 'PUT', body: await f.arrayBuffer(), headers: { 'x-amz-meta-start_time': startTime, 'x-amz-meta-end_time': endTime, 'x-amz-meta-tutor_id': tutorId, 'x-amz-meta-title': title } });
    if (!putRes.ok) {
      const errBody = await putRes.text().catch(() => '');
      console.error(`S3 upload failed: HTTP ${putRes.status}`, errBody);
      throw new Error(`S3 upload failed (HTTP ${putRes.status}${errBody ? `: ${errBody}` : ''})`);
    }
    return { success: true };
  };

  const startAnalysis = async (sessionId, cId) => {
    if (isTokenExpired) await isTokenExpired();
    const authToken = getAuthToken();
    const sessionIdNoExt = sessionId.includes('.') ? sessionId.substring(0, sessionId.lastIndexOf('.')) : sessionId;
    const res = await fetch(`${API_BASE_URL}v1/analyze`, { method: 'POST', headers: { Authorization: authToken }, body: JSON.stringify({ clientId: cId, processingType: PIPELINE_ID, sessionId: sessionIdNoExt, useExisting: false, requestURL: '', reasonCode: 'new' }) });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const data = await res.json();
    let executionId = '';
    if (typeof data === 'string') { const match = data.match(/Execution id is ([a-f0-9-]+)/i); if (match) executionId = match[1]; }
    return { success: true, data, executionId };
  };

  const postAnalysisStatus = async (sessionId, executionId, metadata) => {
    if (isTokenExpired) await isTokenExpired();
    const authToken = getAuthToken();
    // Use the trimmed title/description from metadata, not the raw form state.
    const res = await fetch(`${API_BASE_URL}v1aux/mediaAnalysisStatus`, {
      method: 'POST',
      headers: { Authorization: authToken, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        userId,
        uploadTime: metadata.uploadTimestamp,
        tutorId,
        clientId,
        description: metadata.description,
        title: metadata.title,
        executionId,
        uploadSource,
      }),
    });
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return await res.json();
  };

  const handleUpload = async () => {
    const errors = [];
    if (!tutorId.trim()) errors.push('Email could not be retrieved.');
    if (!title.trim()) errors.push('A title is required.');
    if (!file) errors.push('A file is required.');
    if (!duration || duration <= 0) errors.push('File must have a valid duration.');
    if (errors.length > 0) { setError(errors.join(' ')); return; }

    const freshMetadata = generateSessionMetadata(file, description, tutorId, clientId, duration, title);

    mixpanel.track("uploadStarted", {
      fileName: freshMetadata.fileName,
      fileSize: freshMetadata.fileSize,
      fileType: freshMetadata.fileType,
      duration: freshMetadata.duration,
    });

    setIsUploading(true); setUploadProgress(0); setError('');
    try {
      const sessionId = freshMetadata.fileName;
      // Step 1: Upload to S3 (0% → 60%)
      setUploadProgress(5);
      try {
        await uploadToS3(file, sessionId, freshMetadata);
      } catch (e) {
        mixpanel.track("uploadFailed", { fileName: freshMetadata.fileName, stage: 'S3 upload', error: e.message || 'Unknown error' });
        throw e;
      }
      setUploadProgress(60);
      // Step 2: Start Analysis (60% → 90%)
      try {
        setUploadProgress(65);
        const analysisResult = await startAnalysis(sessionId, clientId);
        setUploadProgress(90);

        if (analysisResult.success) {
          const sessionIdNoExt = sessionId.includes('.') ? sessionId.substring(0, sessionId.lastIndexOf('.')) : sessionId;

          // Step 3: Post Analysis Status (90% → 100%)
          let statusPostFailed = false;
          try {
            setUploadProgress(95);
            await postAnalysisStatus(sessionIdNoExt, analysisResult.executionId || '', freshMetadata);
            setUploadProgress(100);
          } catch (e) {
            console.error(e);
            statusPostFailed = true;
            setUploadProgress(100);
          }
          await new Promise(resolve => setTimeout(resolve, 600));
          mixpanel.people.increment('uploadsCompleted');
          mixpanel.track("uploadCompleted", {
            fileName: freshMetadata.fileName,
            fileSize: freshMetadata.fileSize,
            duration: freshMetadata.duration,
            statusPostFailed,
          });

          resetForm();
          showSuccessToast();
          setActiveMenu(MENU_IDS.RECORDINGS);
        }
      } catch (e) {
        mixpanel.track("uploadFailed", { fileName: freshMetadata.fileName, stage: 'Analysis start', error: e.message || 'Unknown error' });
        showErrorToast('Please try uploading your file again or another file.');
      }
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const isFormValid = tutorId.trim() && title.trim() && file && duration && duration > 0;


  const chipSx = {
    borderRadius: '999px',
    border: '1px solid #d1d5db',
    backgroundColor: 'transparent',
    fontSize: '11px',
    height: '26px',
      
    color: COLORS.text,
  };

  // Compute estimated start/end for the file card display only — not used for upload.
  const fileCardStartTime = file ? file.lastModified : null;
  const fileCardEndTime = file && duration ? file.lastModified + duration * 1000 : null;

  return (
    <>
      <AppSnackbar toast={toast} onClose={() => setToast(null)} />

      {showWelcomeDialog && (
        <UploadHelpDialog onClose={() => setShowWelcomeDialog(false)} />
      )}

      <Box sx={{ mt: '10px', mb: '10px' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 400, color: COLORS.text, fontFamily: FONT_HEADING, mb: isMobile ? 1 : 0 }}>
            Upload Recording
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => setShowWelcomeDialog(true)}>
            <Typography sx={{ fontSize: '14px', color: COLORS.primary, fontWeight: 500,    }}>
              Help me upload a recording
            </Typography>
            <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
              i
            </Box>
          </Box>
        </Box>

        <Box sx={{ padding: isMobile ? '0' : '0px 32px', maxWidth: 780, margin: '0 auto' }}>

          {/* Recording Title */}
          <TextField
            fullWidth variant="outlined" label="Recording Title*"
            value={title} onChange={(e) => { if (e.target.value.length <= 100) setTitle(e.target.value); }}
            onBlur={() => setTitle((t) => t.trim())}
            placeholder="e.g. Math class - Week 3"
            helperText={`${title.length}/100 characters`} inputProps={{ maxLength: 100 }}
            sx={{ mb: 2,    '& .MuiOutlinedInput-root': { borderRadius: '8px',    }, '& .MuiInputLabel-root': {    } }}
          />

          {/* Description */}
          <TextField
            fullWidth multiline rows={isMobile ? 3 : 4} variant="outlined" label="Recording description (optional)"
            value={description} onChange={(e) => { if (e.target.value.length <= 256) setDescription(e.target.value); }}
            placeholder={isMobile ? 'Topic, class period, or notes...' : 'Describe this recording (topic, class period, section, or notes to help you find it later, e.g. "6th Grade Math, Period 4 — Fractions: adding unlike denominators")'}
            helperText={`${description.length}/256 characters`} inputProps={{ maxLength: 256 }}
            sx={{ mb: 3,    '& .MuiOutlinedInput-root': { borderRadius: '8px',    }, '& .MuiInputLabel-root': {    } }}
          />

          {!file && (
            <>
              {isMobile ? (
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: `2px dashed ${COLORS.primary}`,
                    borderRadius: '12px',
                    padding: '36px 24px',
                    textAlign: 'center',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    mb: 2,
                    '&:active': { backgroundColor: '#eff6ff' },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: '50%', backgroundColor: COLORS.primary, mb: 2 }}>
                    <CloudUpload sx={{ fontSize: 26, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#111827', mb: 1,    }}>
                    Tap to choose a file
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: COLORS.textMuted,    }}>
                    Audio, video, or transcript (.vtt, .srt)
                  </Typography>
                  <input
                    ref={fileInputRef} type="file"
                    onChange={handleFileSelect}
                    accept={ACCEPTED_AUDIO_AND_TRANSCRIPT_TYPES}
                    style={{ display: 'none' }}
                  />
                </Box>
              ) : (
                <Box
                  onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: `2px dashed ${isDragging ? COLORS.primaryHover : COLORS.primary}`,
                    borderRadius: '12px', padding: '48px 32px', textAlign: 'center',
                    backgroundColor: isDragging ? '#eff6ff' : 'transparent',
                    cursor: 'pointer', transition: 'all 0.2s ease', mb: 2,
                    '&:hover': { backgroundColor: '#f0f7ff' },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', backgroundColor: COLORS.primary, mb: 2 }}>
                    <CloudUpload sx={{ fontSize: 28, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontSize: '15px', color: COLORS.text, mb: 1.5,    }}>
                    Drop your audio recording or transcript here or click to select
                  </Typography>
                  <Button
                    variant="contained" size="small"
                    sx={{ borderRadius: '4px', textTransform: 'none',    fontSize: '13px', px: 2.5, backgroundColor: COLORS.primaryDark, boxShadow: 'none', '&:hover': BUTTON_HOVER_SX }}
                  >
                    Choose File
                  </Button>
                  <input ref={fileInputRef} type="file" onChange={handleFileSelect} accept={ACCEPTED_AUDIO_AND_TRANSCRIPT_TYPES} style={{ display: 'none' }} />
                </Box>
              )}

              {/* Empty file bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', borderRadius: '8px', padding: '10px 14px', mb: 3, backgroundColor: '#F6F1ED', gap: 1 }}>
                <AudioFileOutlinedIcon sx={{ color: '#9ca3af', fontSize: 22 }} />
                <Typography sx={{ fontSize: '13.5px', color: '#9ca3af',    }}>
                  No selected File -
                </Typography>
              </Box>
            </>
          )}

          {file && duration && (
            <>
              <Box sx={{ border: `1px solid ${COLORS.hairline}`, borderRadius: '10px', backgroundColor: '#F6F1ED', p: '14px 16px', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <AudioFileOutlinedIcon sx={{ color: COLORS.text, fontSize: 24, mr: 1.5, flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#111827',    flex: 1, wordBreak: 'break-all' }}>
                    {file.name}
                  </Typography>
                  <Box onClick={removeFile} sx={{ cursor: 'pointer', color: COLORS.textMuted, display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0, '&:hover': { color: '#ef4444' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 22 }} />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Chip label={`Size: ${formatBytes(file.size)}`} sx={chipSx} />
                  <Chip label={`Duration: ${formatDuration(duration)}`} sx={chipSx} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Chip label={`Start: ${formatDateTime(fileCardStartTime)}`} sx={chipSx} />
                  <Chip label={`End (est.): ${formatDateTime(fileCardEndTime)}`} sx={chipSx} />
                </Box>
              </Box>

              <input ref={fileInputRef} type="file" onChange={handleFileSelect} accept={ACCEPTED_AUDIO_AND_TRANSCRIPT_TYPES} style={{ display: 'none' }} />
            </>
          )}

          {error && <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2,    }}>{error}</Alert>}
          {warning && <Alert severity="warning" sx={{ mb: 2,    }}>{warning}</Alert>}

          {isUploading && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 1.5,    fontSize: '13.5px', fontWeight: 500 }}>
                Your recording is uploading. Please keep this window open until the upload is complete.
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{    }}>Uploading...</Typography>
                <Typography variant="body2" sx={{    color: COLORS.primary, fontWeight: 600 }}>{uploadProgress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 4 }} />
            </Box>
          )}

          <Button
            fullWidth variant="contained"
            onClick={handleUpload}
            disabled={!isFormValid || isUploading}
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              py: isMobile ? 2 : 1.6,
              fontSize: isMobile ? '15px' : '14px',
              fontWeight: 600, textTransform: 'none',   
              borderRadius: '4px', boxShadow: 'none',
              backgroundColor: COLORS.primaryDark,
              '&:hover': BUTTON_HOVER_SX,
              '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: '#FDFBFA' },
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload Recording and Generate Talk Tree'}
          </Button>

        </Box>
      </Box>
    </>
  );
};

export default SessionUpload;